import './DashboardLayout.css';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import DashboardMenu from './dashboard_layout/menu/DashboardMenu';
import DashboardCurtain from './dashboard_layout/curtain/DashboardCurtain';
import { useContext, useEffect, useRef, useState } from 'react';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import DashboardHeader from './dashboard_layout/header/DashboardHeader';
import {
  DESKTOP_MENU_WIDTH,
  EnvironmentBanner,
  FLOATING_MENU_WIDTH,
  getEnvironmentBannerMode,
} from '@leedpulse-monorepo/components';
import classNames from 'classnames';
import { SIGN_IN_PAGE_PATH } from '../pages/auth/SignInPage';
import { SETTINGS_PAGE_PATH } from '../pages/settings/SettingsPage';
import AuthenticationContext, { AuthenticationState } from '../contexts/auth/AuthenticationContext';
import LoadingCurtain from '../components/@shared/LoadingCurtain';
import PasswordChangeForm from '../components/password-change-page/PasswordChangeForm';
import AppContext from '../contexts/data/AppContext';
import PageScaffoldContext from '../contexts/ui/PageScaffoldContext';
import { PUBLIC_ROUTES } from '../AppRouter';
import { metadata } from '@leedpulse-monorepo/firebase-metadata';

const LOCAL_STORAGE_MENU_COLLAPSED_KEY = 'leedflow-menu-collapsed';

interface DashboardLayoutProps {}

const DashboardLayout: React.FC<DashboardLayoutProps> = (_) => {
  const authContext = useContext(AuthenticationContext);
  const appContext = useContext(AppContext);
  const pageScaffoldContext = useContext(PageScaffoldContext);
  const navigate = useNavigate();
  const location = useLocation();
  const windowSize = useWindowSize();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [isMenuCollapsed, setIsMenuCollapsed] = useState<boolean>(
    () => localStorage.getItem(LOCAL_STORAGE_MENU_COLLAPSED_KEY) === '1'
  );
  const isFirstRender = useRef(true);

  // Handle window resize
  useEffect(() => {
    // Skip if windowSize not yet initialized (returns 0 on first render)
    if (windowSize.width === 0) return;

    if (isMobileMenuOpen) {
      setMobileMenuOpen(false);
    }
    if (windowSize.width < FLOATING_MENU_WIDTH && !isMenuCollapsed) {
      setIsMenuCollapsed(true);
    } else if (windowSize.width > DESKTOP_MENU_WIDTH && isMenuCollapsed && !isFirstRender.current) {
      const localValue = localStorage.getItem(LOCAL_STORAGE_MENU_COLLAPSED_KEY) === '1';
      setIsMenuCollapsed(localValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowSize.width]);

  // Restore sidebar when leaving settings page
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (location.pathname !== SETTINGS_PAGE_PATH && windowSize.width > DESKTOP_MENU_WIDTH) {
      const localValue = localStorage.getItem(LOCAL_STORAGE_MENU_COLLAPSED_KEY) === '1';
      setIsMenuCollapsed(localValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (authContext.authenticationState === AuthenticationState.UNAUTHENTICATED &&
      !PUBLIC_ROUTES.some((route) => route.path === location.pathname)
    ) {
      navigate(SIGN_IN_PAGE_PATH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authContext.authenticationState]);

  useEffect(() => {
    appContext.fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pageScaffoldContent = pageScaffoldContext?.contentElement;
    // Don't apply padding on settings page (it uses noPadding)
    if (location.pathname === SETTINGS_PAGE_PATH || !pageScaffoldContent) {
      return;
    }
    if (windowSize.width < FLOATING_MENU_WIDTH) {
      return;
    }
    pageScaffoldContent.style.padding = isMenuCollapsed
      ? '32px 32px 32px 96px'
      : '32px 32px 32px 304px';
  }, [isMenuCollapsed, location.pathname, pageScaffoldContext?.contentElement, windowSize.width, authContext.authenticationState]);

  return authContext.authenticationState !== AuthenticationState.AUTHENTICATED ? (
    <LoadingCurtain />
  ) : authContext.isPasswordChangeRequired ? (
    <PasswordChangeForm />
  ) : (
    <div className="dashboard-layout">
      <EnvironmentBanner mode={getEnvironmentBannerMode(metadata('frontendleedflow'))} />
      <DashboardMenu
        show={isMobileMenuOpen}
        isCollapsed={isMenuCollapsed}
        onNavigate={() => setMobileMenuOpen(false)} // wtf ?
        onCollapseChange={(value) => {
          setIsMenuCollapsed(value);
          localStorage.setItem(LOCAL_STORAGE_MENU_COLLAPSED_KEY, value ? '1' : '0');
        }}
      />
      <DashboardCurtain show={isMobileMenuOpen} onClick={() => setMobileMenuOpen(false)} />
      {windowSize.width < FLOATING_MENU_WIDTH && (
        <DashboardHeader onMenuClick={() => setMobileMenuOpen(true)} />
      )}
      <main
        className={classNames('dashboard-layout__content', {
          'dashboard-layout__content--collapsed': isMenuCollapsed,
        })}
      >
        <Outlet />
      </main>
    </div>
  );
};

export default DashboardLayout;
